/**
 * Picture Component
 */

import './styles.scss';

import * as classNames from 'classnames';
import * as imageloaded from 'imagesloaded';
import { find } from 'lodash';
import * as nextTick from 'next-tick';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import * as WindowSizeListener from 'react-window-size-listener';
import { VelocityComponent } from 'velocity-react';

interface IOwnProps {
  alt?: string;
  assets: IResponsiveImage;
  className?: string;
  onLoad?: (img?: HTMLImageElement) => void;
}

interface IOwnState {
  fillWith: 'width' | 'height';
  initialized: boolean;
}

const classes = new BEM('picture');

export default class Picture extends React.PureComponent<IOwnProps, IOwnState> {
  private element: HTMLPictureElement;
  private image: HTMLImageElement;
  private imageRatio: number;
  private imageWidth: number;
  private imageHeight: number;

  constructor(props: IOwnProps) {
    super(props);

    const metadata = props.assets.large[0].metadata;
    this.imageHeight = metadata.height;
    this.imageWidth = metadata.width;

    this.imageRatio = this.imageWidth / this.imageHeight;

    this.state = {
      fillWith: 'width',
      initialized: false,
    };
  }

  componentDidMount() {
    imageloaded(this.element, () => {
      if (this.image) {
        nextTick(() => {
          this.onInit();
        });
      }
    });
  }

  onInit = () => {
    const { width, height } = this.image.getBoundingClientRect();
    const ratio = width / height;

    if (ratio && ratio !== Infinity) {
      if (ratio !== this.imageRatio) {
        this.imageRatio = ratio;
        this.onResize();
      }
      this.setState({ initialized: true });
    } else {
      setTimeout(this.onInit, 100);
    }
  }

  onResize = () => {
    const { width, height } = this.element.getBoundingClientRect();
    const pictureRatio = width / height;

    if (pictureRatio >= this.imageRatio) {
      this.setState((prevState) => {
        return prevState.fillWith !== 'width' ? { ...this.state, fillWith: 'width' } : prevState;
      });
    } else {
      this.setState((prevState) => {
        return prevState.fillWith !== 'height' ? { ...this.state, fillWith: 'height' } : prevState;
      });
    }
  }

  render() {
    const {
      alt,
      assets,
      className,
    } = this.props;

    const xlargeWebp = find(assets.xlarge, { format: 'webp' });
    const xlargeImg = find(assets.xlarge, ({ format }) => format !== 'webp')!;
    const largeWebp = find(assets.large, ({ format }) => format === 'webp');
    const largeImg = find(assets.large, ({ format }) => format !== 'webp')!;
    const mediumWebp = find(assets.medium, ({ format }) => format === 'webp');
    const mediumImg = find(assets.medium, ({ format }) => format !== 'webp')!;
    const smallWebp = find(assets.small, ({ format }) => format === 'webp');
    const smallImg = find(assets.small, ({ format }) => format !== 'webp')!;
    const blurredImg = find(assets.blur, ({ format }) => format !== 'webp')!;

    const classesModifiers = {
      [`fill-with-${this.state.fillWith}`]: true,
    };

    const extraClasses = classNames(className);

    return (
      <VelocityComponent animation={this.state.initialized ? 'transition.fadeIn' : { opacity: 0, display: 'none' }}>
        <picture ref={(node) => this.element = node!} {...classes('', classesModifiers, extraClasses) }>
          {largeWebp && xlargeWebp && (
            <source type="image/webp" srcSet={`${largeWebp.path}, ${xlargeWebp.path} 2x`} media="(min-width: 1440px)" />
          )}
          {mediumWebp && largeWebp && (
            <source type="image/webp" srcSet={`${mediumWebp.path}, ${largeWebp.path} 2x`} media="(min-width: 900px)" />
          )}
          {smallWebp && mediumWebp && (
            <source type="image/webp" srcSet={`${smallWebp.path}, ${mediumWebp.path} 2x`} />
          )}
          <source srcSet={`${largeImg.path}, ${xlargeImg.path} 2x`} media="(min-width: 1440px)" />
          <source srcSet={`${mediumImg.path}, ${largeImg.path} 2x`} media="(min-width: 900px)" />
          <source srcSet={`${smallImg.path}, ${mediumImg.path} 2x`} />
          <img ref={(node) => this.image = node!} src={blurredImg.path} alt={alt} {...classes('image') } />
          <WindowSizeListener onResize={this.onResize} />
        </picture>
      </VelocityComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/images/Picture/index.tsx